import type { Artist, DecoratedScheduleEntry, FestivalData, FestivalEvent } from '../lib/types.ts';
import { getProgramArtists } from './program.ts';

// Canonical festival weekday order (mirrors festivalSelectors' day filter options) so the day
// tabs always read chronologically — Fredag before Lørdag — regardless of the order artists
// happen to appear in the data. Unknown day names sort last, keeping their relative order.
const DAY_ORDER = ['Torsdag', 'Fredag', 'Lørdag', 'Søndag'];

export function getScheduleDays(artists: readonly Artist[]): string[] {
  const days = [...new Set(artists.map(a => a.festivalDayName).filter(Boolean))];
  if (!days.length) return ['Fredag', 'Lørdag'];
  const rank = (d: string) => {
    const i = DAY_ORDER.indexOf(d);
    return i === -1 ? DAY_ORDER.length : i;
  };
  return days.sort((a, b) => rank(a) - rank(b));
}

export function getScheduleEntriesForDay(
  entries: readonly DecoratedScheduleEntry[],
  day: string,
): DecoratedScheduleEntry[] {
  return entries.filter(e => e.festivalDayName === day);
}

/** One rendered day column: the day name plus its chronological entries (markers included).
 *  `key` is unique across feeds — the same day name can occur in more than one feed (Torsdag
 *  exists in both the Community day and Ung i Midten feeds). */
export interface ScheduleDayColumnData {
  readonly key: string;
  readonly day: string;
  readonly entries: readonly DecoratedScheduleEntry[];
}

/** Synthetic open/close marker entry derived from a day's opening/closing hours. */
function makeMarker(day: string, kind: 'open' | 'close', time: string, title: string): DecoratedScheduleEntry {
  return {
    id: `${day}-${kind}`,
    artistId: '',
    title,
    startTime: time,
    endTime: time,
    stageName: '',
    festivalDayName: day,
    kind: 'marker',
  };
}

/**
 * Computes the day columns for one feed's dataset. Returns [] when the feed has no real acts yet
 * (synthetic open/close markers never count) so the caller can show the "not ready" placeholder —
 * an act-less feed must not render day columns containing nothing but opening/closing times.
 */
export function buildScheduleDayColumns(
  data: FestivalData,
  { restrictToDays, eventType, keyPrefix = '' }: {
    restrictToDays?: readonly string[];
    /** Which event's days supply the open/close markers. Community day and Ung i Midten both fall
     *  on "Torsdag", so a day name alone is ambiguous — a feed belonging to one event must say
     *  which. Absent = match by day name across every event (the mixed /program view). */
    eventType?: FestivalEvent['type'];
    keyPrefix?: string;
  } = {},
): ScheduleDayColumnData[] {
  const allDaysUnfiltered = getScheduleDays(getProgramArtists(data.program));
  const allDays = restrictToDays && restrictToDays.length > 0
    ? allDaysUnfiltered.filter(d => restrictToDays.includes(d))
    : allDaysUnfiltered;

  // Festival open/close markers are derived from each day's opening/closing hours (program.json
  // event days). Feeds that already carry their own markers (Community day / Ung i Midten) are
  // left untouched so we never double them up.
  const eventDays = (data.program.events ?? [])
    .filter(e => !eventType || e.type === eventType)
    .flatMap(e => e.days);
  const openCloseMarkers = (dayName: string, closeLabel: string): DecoratedScheduleEntry[] => {
    const d = eventDays.find(x => x.name === dayName && (x.openingTime || x.closingTime));
    if (!d) return [];
    const markers: DecoratedScheduleEntry[] = [];
    if (d.openingTime) markers.push(makeMarker(dayName, 'open', d.openingTime, 'Festivalpladsen åbner'));
    if (d.closingTime) markers.push(makeMarker(dayName, 'close', d.closingTime, closeLabel));
    return markers;
  };

  // Only the festival's own final day earns the send-off message; Community day / Ung i Midten
  // (and any earlier festival day) just say the grounds are closing.
  const festivalDays = (data.program.events ?? []).find(e => e.type === 'festival')?.days ?? [];
  const festivalLastDay = festivalDays.length
    ? festivalDays.reduce((a, b) => (a.date >= b.date ? a : b)).name
    : undefined;

  const days = allDays.map((day) => {
    const dayEntries = getScheduleEntriesForDay(data.scheduleEntries, day);
    // Only real acts decide whether the schedule is "ready"; the synthetic open/close markers
    // below must never count towards that (see totalRealEntries).
    const realCount = dayEntries.filter(e => e.kind !== 'marker').length;
    const closeLabel = day === festivalLastDay ? 'Festivalen slutter. Tak for i år!' : 'Festivalpladsen lukker';
    const entries = dayEntries.some(e => e.kind === 'marker')
      ? dayEntries
      : [...dayEntries, ...openCloseMarkers(day, closeLabel)];
    return { key: keyPrefix + day, day, entries, realCount };
  });
  const totalRealEntries = days.reduce((n, d) => n + d.realCount, 0);
  return totalRealEntries === 0 ? [] : days;
}
